/**
 * Offline Driver Workspace coordinator (Phase 13.8)
 *
 * Additive glue only. It decides *when* the existing prefetch/snapshot
 * engine runs, and cleans up cached company data on sign-out.
 *
 * Triggers: app boot, sign-in, foreground resume, connectivity restored,
 * manual refresh. All passes are debounced and failure-tolerant — a failed
 * pass never blocks the UI and never discards queued work.
 *
 * Native-only by default so desktop/web behaviour is byte-for-byte unchanged.
 */

import { supabase } from "@/integrations/supabase/client";
import { isNative } from "./capacitor";
import { syncDriverWorkspace } from "./driverJobSync";
import { clearDriverPack } from "./driverOfflinePack";
import { jobsCache, jobDrafts, syncQueue } from "./offlineDb";
import { isKnownOffline } from "./connectivity";

const DEBOUNCE_MS = 20_000;
let lastRunAt = 0;
let running = false;
let started = false;

/** Run one workspace sync pass (debounced, never throws). */
export async function runWorkspaceSync(reason: string, force = false): Promise<void> {
  if (!isNative) return;
  if (running) return;
  if (!force && Date.now() - lastRunAt < DEBOUNCE_MS) return;
  if (isKnownOffline()) return;

  running = true;
  lastRunAt = Date.now();
  try {
    console.log(`[OfflineWorkspace] Sync pass (${reason})`);
    await syncDriverWorkspace();
  } catch (err) {
    console.warn(`[OfflineWorkspace] Sync pass failed (${reason}):`, err);
  } finally {
    running = false;
  }
}

/**
 * Sign-out cleanup.
 * Clears cached company data (jobs, snapshots, reference pack) but PRESERVES
 * the upload queue and its drafts so no captured photo/signature is ever lost;
 * they resume after re-authentication under the existing security model.
 */
export async function clearDriverOfflineData(): Promise<void> {
  try {
    const pending = await syncQueue.getAll();
    const protectedJobs = new Set(pending.map((p) => p.job_id));

    await jobsCache.clear().catch(() => undefined);
    await clearDriverPack();

    const { getAllSnapshots, deleteSnapshot } = await import("./jobSnapshotManager");
    const snapshots = await getAllSnapshots();
    for (const s of snapshots) {
      if (protectedJobs.has(s.job_id)) continue;
      await deleteSnapshot(s.job_id);
    }

    const drafts = await jobDrafts.getAll();
    for (const d of drafts) {
      if (protectedJobs.has(d.job_id)) continue;
      await jobDrafts.delete(d.job_id).catch(() => undefined);
    }

    console.log(
      `[OfflineWorkspace] Cleared cached data on sign-out (${pending.length} queued items preserved)`
    );
  } catch (err) {
    console.warn("[OfflineWorkspace] Sign-out cleanup failed:", err);
  }
}

/** Register triggers. Safe to call once on boot. */
export function initOfflineWorkspace(): void {
  if (started || !isNative) return;
  started = true;

  const kick = (reason: string) => () => {
    void runWorkspaceSync(reason);
  };

  window.addEventListener("online", kick("online"));
  window.addEventListener("network-status-change", ((e: Event) => {
    const detail = (e as CustomEvent<{ connected?: boolean }>).detail;
    if (detail?.connected !== false) void runWorkspaceSync("network-restored");
  }) as EventListener);

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") void runWorkspaceSync("foreground");
  });

  supabase.auth.onAuthStateChange((event) => {
    if (event === "SIGNED_IN" || event === "TOKEN_REFRESHED") {
      void runWorkspaceSync("auth:" + event, event === "SIGNED_IN");
    }
    if (event === "SIGNED_OUT") {
      void clearDriverOfflineData();
    }
  });

  // Initial pass shortly after boot so today's work is cached before the
  // driver leaves signal.
  setTimeout(() => void runWorkspaceSync("boot", true), 2_000);
}
